import { message } from 'antd';
import moment, { isMoment } from 'moment-timezone';

import { IBuilderSaveResult } from 'components/EmbeddedBuilder';
import {
  cleanOutcomeOptions,
  doesRuleTriggerDayHasValue,
  normalizeVoucherExpiryEvent
} from 'containers/Rule/form-v2/utils';
import { TRuleCreatePage } from 'containers/Rule/i18n/TModules';
import i18n from 'utils/i18n';
import API from 'utils/request';

import { ConfigIntent } from '../configStudio/configSubmission';
import { TransactionRuleValues } from './ruleAdapter';

// The module's own conversions (`Rule/form-v2/index.tsx`, inner functions there): a wall time in
// the tenant zone, sent as UTC.
const toTenantInstant = (value: unknown, timezone: string): unknown => {
  if (!value) return value;
  const local = isMoment(value) ? value : moment(value as string);
  return moment.tz(local.format('YYYY-MM-DD HH:mm:ss'), timezone).toISOString();
};

const timeToUtc = (time: string, timezone: string) =>
  time
    ? moment
        .tz(`${moment().format('YYYY-MM-DD')} ${time}`, timezone)
        .utc()
        .format('HH:mm:ss')
    : '';

/** The body the module's create step posts for these form values. */
export const toRulePayload = (
  values: TransactionRuleValues,
  intent: ConfigIntent,
  timezone: string
): Record<string, unknown> => {
  const payload = normalizeVoucherExpiryEvent({
    ...values,
    ...(intent === 'draft' ? { save_as_draft: true } : {}),
    temp: undefined,
    // The tenant zone is applied below; the module's body carries no zone of its own.
    timezone: undefined,
    begins_at: toTenantInstant(values.begins_at, timezone),
    ends_at: values.ends_at ? toTenantInstant(values.ends_at, timezone) : values.ends_at,
    outcome_type: values.outcome_type === 'Push Event Only' ? null : values.outcome_type,
    // The module form always holds an object here; only push and customer outcomes fill it.
    outcome_options: cleanOutcomeOptions(values.outcome_options ?? {})
  });
  if (payload.outcome_type === 'User::Account') {
    payload.outcome_options = {
      ...payload?.outcome?.outcome_options,
      outcome_properties: payload?.outcome?.outcome_options?.outcome_properties || []
    };
  }
  if (Array.isArray(payload.rule_trigger_days)) {
    const days = payload.rule_trigger_days
      .map(item =>
        item.attribute === 'transaction_time' && typeof item.selected_values === 'string'
          ? { ...item, selected_values: timeToUtc(item.selected_values, timezone) }
          : item
      )
      .filter(doesRuleTriggerDayHasValue);
    payload.rule_trigger_days = days.length === 0 ? null : days;
  }
  return payload;
};

const t = (key: string) => i18n.t(`${TRuleCreatePage}.${key}`);

/**
 * Creates the rule through the module's endpoint (`POST /v4/dash/transaction_rules`, as
 * `Rule/form-v2/index.tsx`); `meta.rule_request_id` marks a maker-checker request. Rejects with
 * the API's message, which the review shows inline.
 */
export const saveRule = async (
  values: TransactionRuleValues,
  intent: ConfigIntent,
  timezone: string
): Promise<IBuilderSaveResult> => {
  let response;
  try {
    ({ data: response } = await API.post(
      '/v4/dash/transaction_rules',
      toRulePayload(values, intent, timezone)
    ));
  } catch (error) {
    throw new Error(
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('EditMessage.TransactionRulesRequestError')
    );
  }
  if (!response?.data?.id) {
    throw new Error(t('EditMessage.TransactionRulesError'));
  }
  const requestId: number | null = response?.meta?.rule_request_id ?? null;
  message.success(
    t(
      requestId !== null
        ? 'EditMessage.TransactionRulesMakerCheckerSuccess'
        : 'EditMessage.TransactionRulesSuccess'
    )
  );
  return {
    entity: 'rule',
    id: response.data.id,
    pendingRequestId: requestId,
    savedAsDraft: intent === 'draft'
  };
};
